import { useState } from "react";

import { Check, ChevronsUpDown } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { countryFlag } from "@/pages/tina/runs/components/site-picker/utils";

/** A city with its country, whatever the caller calls the fields. */
export interface CityPick {
  city: string;
  countryCode: string;
}

export interface CitySearchSelectProps {
  /** Every city with its country, across all countries. */
  options: CityPick[];
  /** The chosen city, or null for "All cities". */
  city: string | null;
  /** The country the picker is in, to tell two cities with one name apart. */
  country: string | null;
  /** Null clears the city; a pick also carries the country to switch to. */
  onChange: (pick: CityPick | null) => void;
}

/**
 * City box that searches across every country. Picking "Kaunas · LT" sets the
 * city and its country together, so the curator never picks the country first.
 */
export function CitySearchSelect({
  options,
  city,
  country,
  onChange,
}: CitySearchSelectProps) {
  const [open, setOpen] = useState(false);

  const isSelected = (option: CityPick) =>
    option.city === city && option.countryCode === country;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-label="City"
          className="h-9 justify-between font-normal"
        >
          <span className="truncate">{city ?? "All cities"}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[280px] p-0">
        <Command>
          <CommandInput placeholder="Search any city…" />
          <CommandList>
            <CommandEmpty className="justify-center p-2">
              No city found
            </CommandEmpty>
            <CommandGroup>
              <CommandItem
                value="all cities"
                onSelect={() => {
                  onChange(null);
                  setOpen(false);
                }}
              >
                <Check
                  className={cn(
                    "mr-2 h-4 w-4",
                    city === null ? "opacity-100" : "opacity-0",
                  )}
                />
                All cities
              </CommandItem>
              {options.map((option) => (
                <CommandItem
                  key={`${option.city}|${option.countryCode}`}
                  value={`${option.city} ${option.countryCode}`}
                  onSelect={() => {
                    onChange(option);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      isSelected(option) ? "opacity-100" : "opacity-0",
                    )}
                  />
                  <span className="truncate">{option.city}</span>
                  <span className="ml-auto pl-2 text-xs text-muted-foreground">
                    {countryFlag(option.countryCode)} {option.countryCode}
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
